import { z } from 'zod';
import { ClientContactRoleSchema } from './user.js';

// Body-corporate types accepted by the eligibility gate (prototype's
// portal-extras.jsx PortalSignup step 1 + Contact page eligibility gate).
// Sole traders and small partnerships are explicitly rejected.
export const ENTITY_TYPES = ['Ltd', 'PLC', 'LLP', 'FZ-LLC', 'Other'] as const;
export const EntityTypeSchema = z.enum(ENTITY_TYPES);
export type EntityType = z.infer<typeof EntityTypeSchema>;

// Country codes used in the prototype (data.jsx GEOGRAPHIES + signup country list)
export const COUNTRY_CODES = [
  'UK', 'AE-MAIN', 'AE-FZ', 'IN', 'SG', 'BVI', 'KY', 'EU', 'SEA', 'NA', 'OTHER',
] as const;
export const CountryCodeSchema = z.enum(COUNTRY_CODES);
export type CountryCode = z.infer<typeof CountryCodeSchema>;

// Sectors offered in the signup wizard (portal-extras.jsx step 1)
export const SECTORS = [
  'Shipping/Logistics',
  'Energy/Renewables',
  'Real Estate',
  'Infrastructure',
  'Commodities',
  'PE/VC',
  'Family Office',
  'Technology',
  'Other',
] as const;
export const SectorSchema = z.enum(SECTORS);
export type Sector = z.infer<typeof SectorSchema>;

// KYC depth + status — admin clients table uses kyc: 'current' | 'refresh' | 'pending'.
// `in_review` was added by the portal-kyc feature: any non-superseded KYC document
// in the per-doc `in_review` state rolls the client status up to `in_review` at
// read time (rollup precedence in src/server/services/kyc.ts).
export const KYC_STATUS = ['pending', 'in_review', 'current', 'refresh', 'expired', 'rejected'] as const;
export const KycStatusSchema = z.enum(KYC_STATUS);
export type KycStatus = z.infer<typeof KycStatusSchema>;

export const KYC_DEPTH = ['simplified', 'standard', 'enhanced'] as const;
export const KycDepthSchema = z.enum(KYC_DEPTH);
export type KycDepth = z.infer<typeof KycDepthSchema>;

export const SANCTIONS_STATUS = ['pending', 'clear', 'flagged'] as const;
export const SanctionsStatusSchema = z.enum(SANCTIONS_STATUS);
export type SanctionsStatus = z.infer<typeof SanctionsStatusSchema>;

// Client lifecycle status — orthogonal to KYC + sanctions (REQ-013).
export const CLIENT_STATUS = ['prospect', 'active', 'suspended', 'deactivated'] as const;
export const ClientStatusSchema = z.enum(CLIENT_STATUS);
export type ClientStatus = z.infer<typeof ClientStatusSchema>;

// The eligibility gate (constraint #1 of the brief) — every flag must be true
// before the client can submit a brief. Enforced at API level. REQ-013 adds
// `not_advice_acknowledged` as the third gate (alongside body-corp + sophisticated).
export const EligibilityFlagsSchema = z.object({
  body_corporate_confirmed: z.boolean(),
  sophisticated_self_cert: z.boolean(),
  not_advice_acknowledged: z.boolean(),
  terms_accepted: z.boolean(),
  accepted_at: z.date().nullable().optional(),
  accepted_by_user_id: z.string().nullable().optional(),
});
export type EligibilityFlags = z.infer<typeof EligibilityFlagsSchema>;

export const ClientSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  trading_name: z.string().nullable().optional(),
  entity_type: EntityTypeSchema,
  registration_number: z.string().min(1),
  country: CountryCodeSchema,
  sector: SectorSchema,
  vertical: z.string().optional(),
  registered_address: z.string().nullable().optional(),
  website: z.string().nullable().optional(),

  status: ClientStatusSchema.default('prospect'),

  eligibility: EligibilityFlagsSchema,

  kyc: KycStatusSchema,
  kyc_depth: KycDepthSchema.default('standard'),
  sanctions_status: SanctionsStatusSchema.default('pending'),

  lead_staff_id: z.string().nullable().optional(),
  joined_at: z.date(),
  lifetime_value: z.number().int().nonnegative().default(0), // pence
  open_requests: z.number().int().nonnegative().default(0),

  created_at: z.date(),
  updated_at: z.date(),
});
export type Client = z.infer<typeof ClientSchema>;

// Input for sign-up wizard submission (portal-extras.jsx PortalSignup)
export const ClientSignupInputSchema = z.object({
  // Step 1 — Company
  legal_name: z.string().min(1, 'Legal company name is required'),
  registration_number: z.string().min(1, 'Company / registration number is required'),
  country: CountryCodeSchema,
  sector: SectorSchema,
  entity_type: EntityTypeSchema,

  // Step 2 — Authorised contact
  contact_name: z.string().min(1, 'Full name is required'),
  contact_role: z.enum([
    'Director',
    'CEO',
    'CFO',
    'General Counsel',
    'Treasurer',
    'Authorised representative',
  ]),
  contact_email: z.string().email('Corporate email is required'),
  contact_phone: z.string().min(5, 'Direct phone is required'),

  // Step 3 — Eligibility gate (all four must be true — REQ-013)
  body_corporate_confirmed: z.literal(true, {
    errorMap: () => ({ message: 'You must confirm body-corporate status.' }),
  }),
  sophisticated_self_cert: z.literal(true, {
    errorMap: () => ({ message: 'Sophisticated / HNW corporate self-certification is required.' }),
  }),
  not_advice_acknowledged: z.literal(true, {
    errorMap: () => ({ message: 'You must acknowledge Omega is not FCA-authorised and does not give investment advice.' }),
  }),
  terms_accepted: z.literal(true, {
    errorMap: () => ({ message: 'Terms of Business must be accepted.' }),
  }),
});
export type ClientSignupInput = z.infer<typeof ClientSignupInputSchema>;

// ── Admin-facing schemas (operator portal) ──────────────────────────────────

// POST /api/clients — operator creates a body-corporate Client. All four FCA
// gates (REQ-013) are enforced via z.literal(true). Tightened body shape lands
// in Phase 1B; this schema is exported now so frontend + future routes can
// import it without churn.
export const AdminClientCreateInputSchema = z.object({
  legal_name: z.string().min(1).max(200),
  trading_name: z.string().max(200).optional(),
  entity_type: EntityTypeSchema,
  registration_number: z.string().min(1).max(60),
  country: CountryCodeSchema,
  sector: SectorSchema,
  vertical: z.string().max(120).optional(),
  registered_address: z.string().max(500).optional(),
  website: z.string().url().max(200).optional(),
  lead_staff_id: z.string().optional(),
  body_corporate_confirmed: z.literal(true, {
    errorMap: () => ({ message: 'Body-corporate confirmation is required.' }),
  }),
  sophisticated_self_cert: z.literal(true, {
    errorMap: () => ({ message: 'Sophisticated self-certification is required.' }),
  }),
  not_advice_acknowledged: z.literal(true, {
    errorMap: () => ({ message: 'You must acknowledge Omega is not FCA-authorised.' }),
  }),
  terms_accepted: z.literal(true, {
    errorMap: () => ({ message: 'Terms of Business acceptance is required.' }),
  }),
});
export type AdminClientCreateInput = z.infer<typeof AdminClientCreateInputSchema>;

// PATCH /api/clients/:id — operator updates identity / status fields.
// Eligibility and KYC have their own dedicated routes — not mutable here.
export const AdminClientUpdateInputSchema = z.object({
  legal_name: z.string().min(1).max(200).optional(),
  trading_name: z.string().max(200).nullable().optional(),
  registered_address: z.string().max(500).nullable().optional(),
  website: z.string().url().max(200).nullable().optional(),
  lead_staff_id: z.string().nullable().optional(),
  status: ClientStatusSchema.optional(),
}).strict();
export type AdminClientUpdateInput = z.infer<typeof AdminClientUpdateInputSchema>;

// POST /api/clients/:id/contacts — operator invites a User(kind='client').
export const AdminContactInviteInputSchema = z.object({
  email: z.string().email().toLowerCase(),
  name: z.string().min(1).max(120),
  contact_role: ClientContactRoleSchema,
  phone: z.string().min(5).max(40).optional(),
});
export type AdminContactInviteInput = z.infer<typeof AdminContactInviteInputSchema>;
